import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-luxon';
import { useEffect, useMemo, memo, useRef } from 'react';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  registerables,
  TimeScale,
} from 'chart.js';
import { ChartData } from './types';

const scheduleCrosshairDraw = (chart: any) => {
  if (chart._crosshairRaf) return;
  chart._crosshairRaf = requestAnimationFrame(() => {
    chart._crosshairRaf = null;
    chart.draw();
  });
};

const customCrosshair = {
  id: 'customCrosshair',
  afterEvent(chart: any, args: any) {
    const { event } = args;
    if (event.type === 'mouseout') {
      if (chart._crosshairX !== null && chart._crosshairX !== undefined) {
        chart._crosshairX = null;
        scheduleCrosshairDraw(chart);
      }
      return;
    }
    if (event.type === 'mousemove') {
      const { left, right } = chart.chartArea;
      // Pixel-gate: round to whole pixels and skip when the crosshair would land
      // on the same column it is already drawn at, so sub-pixel mouse jitter
      // doesn't schedule needless repaints.
      const nextX = event.x >= left && event.x <= right ? Math.round(event.x) : null;
      if (nextX !== chart._crosshairX) {
        chart._crosshairX = nextX;
        scheduleCrosshairDraw(chart);
      }
    }
  },
  afterDatasetsDraw(chart: any, _args: any, pluginOptions: any) {
    const crosshairX = chart._crosshairX;
    if (crosshairX == null) return;

    const {
      ctx,
      chartArea: { top, bottom },
    } = chart;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(crosshairX, top);
    ctx.lineTo(crosshairX, bottom);
    ctx.strokeStyle = pluginOptions.color || '#919EAB';
    ctx.lineWidth = pluginOptions.width || 1;
    ctx.stroke();
    ctx.restore();
  },
  destroy(chart: any) {
    if (chart._crosshairRaf) {
      cancelAnimationFrame(chart._crosshairRaf);
      chart._crosshairRaf = null;
    }
  },
};

// On-change (stepped) lines hold their value across long gaps with no data points.
// Chart.js auto-scales the y-axis from points within the visible x-range only, so
// zooming/panning into such a gap leaves no in-range point, collapses the y-scale to
// [0, 1], and draws the line off-canvas — the chart looks empty. When the visible range
// contains no real point, anchor the y-axis to the value held across it (the point just
// outside each edge) so the flat segment stays on screen. Only kicks in for stepped
// datasets and only when there's nothing to auto-scale to, leaving normal views untouched.
const keepSteppedLineVisible = {
  id: 'keepSteppedLineVisible',
  afterDataLimits(chart: any, args: any) {
    const { scale } = args;
    if (scale.axis !== 'y') return;
    const xScale = chart.scales?.x;
    if (!xScale) return;
    const { min: xMin, max: xMax } = xScale;

    let heldMin = Infinity;
    let heldMax = -Infinity;
    let hasInRange = false;
    let hasHeld = false;

    chart.data.datasets.forEach((dataset: any, index: number) => {
      if (dataset.stepped !== 'before') return;
      if ((chart.getDatasetMeta(index).yAxisID || 'y') !== scale.id) return;

      const points = dataset.data;
      for (let i = 0; i < points.length; i++) {
        const point = points[i];
        const y = point && typeof point === 'object' ? point.y : point;
        if (y == null || Number.isNaN(y)) continue;

        if (point.x >= xMin && point.x <= xMax) {
          hasInRange = true;
          continue;
        }
        // A point whose value holds into the visible range from either side.
        const holds =
          (point.x < xMin && (i + 1 >= points.length || points[i + 1].x > xMin)) ||
          (point.x > xMax && (i === 0 || points[i - 1].x < xMax));
        if (holds) {
          if (y < heldMin) heldMin = y;
          if (y > heldMax) heldMax = y;
          hasHeld = true;
        }
      }
    });

    // Leave Chart.js' auto-scaling alone whenever the view has real points to scale to.
    if (hasInRange || !hasHeld) return;

    scale.min = heldMin;
    scale.max = heldMax;
    if (scale.min === scale.max) {
      scale.min -= 1;
      scale.max += 1;
    }
  },
};

ChartJS.register(
  ...registerables,
  LineElement,
  CategoryScale,
  TimeScale,
  LinearScale,
  customCrosshair,
  keepSteppedLineVisible,
  zoomPlugin
);

const chartPlugins = [zoomPlugin];

type LineChartProps = {
  chartRef: any;
  options: any;
  allSeries: any;
  extendedColors: string[];
  seriesAxisIds?: string[];
  isLoading?: boolean;
};

const LineChart = memo(
  ({ chartRef, options, allSeries, extendedColors, seriesAxisIds, isLoading }: LineChartProps) => {
    const initializedRef = useRef(false);
    const datasets = useMemo(
      () =>
        allSeries.map((item: ChartData, index: number) => {
          const yAxisID = seriesAxisIds?.[index] ?? (index === 0 ? 'y' : `y${index - 1}`);
          const color = extendedColors[index];
          const isBandEdge = item.seriesVariant === 'min' || item.seriesVariant === 'max';

          return {
            label: item.name,
            // Pass epoch-ms numbers directly; Chart.js TimeScale parses them natively,
            // avoiding one Date allocation per data point on every render.
            data: item.data.map(([timestamp, value]) => ({
              x: timestamp,
              y: value,
            })),
            yAxisID,
            backgroundColor: color,
            borderColor: color,
            borderWidth: 2,
            borderDash: isBandEdge ? [5, 5] : [],
            stepped: item.stepped ? 'before' : false,
            radius: item.data.length === 1 ? 4 : 0,
            pointHoverRadius: item.data.length === 1 ? 6 : 0,
          };
        }),
      [allSeries, extendedColors, seriesAxisIds]
    );
    const userData = useMemo(
      () => ({
        datasets,
      }),
      [datasets]
    );

    useEffect(() => {
      if (chartRef && chartRef.current) {
        chartRef.current.canvas.style.touchAction = 'unset';
      }
    }, [chartRef]);

    useEffect(() => {
      const chart = chartRef?.current;
      if (!chart) return;
      chart._isDataLoading = isLoading;
      chart.draw();
    }, [chartRef, isLoading]);

    useEffect(() => {
      const chart = chartRef?.current;
      if (!chart || !initializedRef.current) {
        initializedRef.current = true;
        return;
      }

      // Datasets carry their own {x, y} points on a time axis, so there are no shared
      // labels to sync — mutate the existing datasets in place to keep Chart.js from
      // re-creating elements (and losing the current zoom/pan) on every data refresh.
      chart.data.datasets.forEach((dataset: any, index: number) => {
        const next = datasets[index];
        if (!next) return;

        dataset.data = next.data;
        dataset.label = next.label;
        dataset.backgroundColor = next.backgroundColor;
        dataset.borderColor = next.borderColor;
        dataset.borderDash = next.borderDash;
        dataset.yAxisID = next.yAxisID;
        // stepped/radius depend on the incoming series (on-change tags, single-point
        // series), so they have to follow the data rather than stay at their first value.
        dataset.stepped = next.stepped;
        dataset.radius = next.radius;
        dataset.pointHoverRadius = next.pointHoverRadius;
      });

      // Handle added/removed datasets
      if (datasets.length !== chart.data.datasets.length) {
        chart.data.datasets = datasets;
      }

      chart.update('none');
    }, [chartRef, datasets]);

    return (
      <Line
        ref={chartRef as React.MutableRefObject<any>}
        data={userData}
        options={options}
        plugins={chartPlugins}
        style={{ maxHeight: '400px', height: '400px' }}
      />
    );
  }
);

export default LineChart;
